import * as THREE from 'three'
import { appBus } from '@/app/app-events'
import { logger } from '@/app/logger'
import { Fraction } from '@/core/fraction'
import {
  CanonicalCut,
  IntegerProblem,
  ensureIntegerMatrix,
  generateAllCuts,
  getCandidateTableauRows,
  getCutInfo,
  translateCutToCanonicalFormStable
} from '@/core/cuts'
import { pi_2dsimplex, pi_2dsimplex_with_mu } from '@/core/cgf'
import { createCutCrossSection, createCuttingPlaneFromCut } from '@/graphics/geometry'
import { visualEffectsManager } from '@/game/effects'
import { type CutHistoryItem, uiManager } from '@/game/ui-manager'
import { useGameStore } from '@/react/state/gameStore'
import { cloneIntegerProblem } from '@/game/engine/problem-clone'
import { countAndDisposeObject3D, countObject3DResources } from '@/game/engine/object3d-resource-utils'

export interface CutWorkflowEngineContext {
  availableCuts: CanonicalCut[]
  cutsRemaining: number
  customCutsLimit: number
  currentProblem: IntegerProblem | null
  originalProblem: IntegerProblem | null
  pendingCut: CanonicalCut | null
  pendingCutIndex: number
  pendingSimplexConfig: { baseCut: CanonicalCut; fVals: number[]; rays: number[][] } | null
  currentCuttingPlane: { visualization: THREE.Mesh; wireframe: THREE.LineSegments; crossSection?: THREE.Object3D } | null
  scene: THREE.Scene
  polyhedronGroup: THREE.Group | null
  initialObjectiveValue: number
  initialIPOptimalValue: number
  initialIPOptimalSolution: number[]
  initialVolume: number
  cutsHistory: CutHistoryItem[]
  cutTypeCounters: Map<string, number>
  camera: THREE.PerspectiveCamera
  cutService: {
    applyCut: (
      problem: IntegerProblem,
      cut: CanonicalCut,
      group: THREE.Group | null,
      originalVolume: number,
      initialObjectiveValue: number,
    ) => {
      problem: IntegerProblem
      group: THREE.Group
      metrics: {
        newVolume: number
        volumeReductionPct: number
        currentObjectiveValue: number
        objectiveReductionPct: number
        optimalSolution: number[]
      }
    }
  }
  polyManager: {
    disposeGroup(group: THREE.Group | null): void
    buildGroupFromInequalities(inequalities: IntegerProblem['inequalities']): THREE.Group
    addIntegerPointsAndAxes(group: THREE.Group, problem: IntegerProblem, options?: { showHull?: boolean }): void
  }
  performanceProbe?: { markStart?: (name: string) => void; markEnd?: (name: string) => void }
  resourceCounters?: {
    addOverlay?: (count?: number) => void
    clearOverlay?: (count?: number) => void
    geometryCreated?: (count?: number) => void
    geometryDisposed?: (count?: number) => void
    materialCreated?: (count?: number) => void
    materialDisposed?: (count?: number) => void
  }
  renderScheduler?: { invalidate?: (reason: string) => void }
  cancelCut: () => void
  openUserSimplexConfigurator(cut: CanonicalCut, cutIndex: number): void
  showCuttingPlanePreview(cut: CanonicalCut): void
  showCutConfirmationButtons(): void
  hideCutConfirmationButtons(): void
  cleanupCuttingPlane(): void
  executeCut(cut: CanonicalCut, originalVolume: number): Promise<void>
  calculateCurrentPolyhedronVolume(): number
  updateUI(): void
  updateBnBStartAvailability(): void
  endGame(): void
  reconstructCompleteProblemFromHistory(): IntegerProblem
  showOptimalSolutionCongratulations(optimalValue: number): void
  validateUserSimplexData(cut: CanonicalCut, problem: IntegerProblem): boolean
  createCutWithMuValue(baseCut: CanonicalCut, muValue: number, fVals: number[], rays: number[][]): CanonicalCut
}

const isBnBInteractionActive = (): boolean => {
  const bnbPhase = useGameStore.getState().ui.bnbState.phase
  return bnbPhase === 'active' || bnbPhase === 'selecting' || bnbPhase === 'branching' || bnbPhase === 'backtracking'
}

export async function applyCut(engine: CutWorkflowEngineContext, cutIndex: number): Promise<void> {
  if (isBnBInteractionActive()) {
    uiManager.showNotification('Branch & Bound is active. Cut operations are disabled.', 'info')
    return
  }

  if (cutIndex >= engine.availableCuts.length) {
    logger.warn(`Cut ${cutIndex + 1} not available`)
    return
  }

  if (engine.cutsRemaining <= 0) {
    logger.warn('No cuts remaining!')
    return
  }

  if (engine.pendingCut) {
    engine.cancelCut()
  }

  const cut = engine.availableCuts[cutIndex]
  useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_SELECTED', cutIndex, cut })

  if (!engine.currentProblem) {
    logger.error('Cannot apply cut: currentProblem is null.')
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_APPLY_FAILED', error: 'Current problem is null' })
    return
  }

  logger.log(`Previewing cut: ${cut.alpha.map((c: number) => Math.round(c)).join('x + ')} <= ${Math.round(cut.beta)}`)

  try {
    if (cut.cgfType === '2dsimplex' && cut.userAdjustable) {
      engine.openUserSimplexConfigurator(cut, cutIndex)
      return
    }

    engine.pendingCut = cut
    engine.pendingCutIndex = cutIndex

    engine.showCuttingPlanePreview(cut)
    engine.showCutConfirmationButtons()
  } catch (error) {
    logger.error('Error previewing cut:', error as any)
    uiManager.showNotification(`Cut Preview Failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    useGameStore.getState().dispatchCutFlowEvent({
      type: 'CUT_APPLY_FAILED',
      error: error instanceof Error ? error.message : 'Unknown preview error',
    })
  }
}

export function showCuttingPlanePreview(engine: CutWorkflowEngineContext, cut: CanonicalCut): void {
  engine.performanceProbe?.markStart?.('cut-preview')
  try {
    const result = createCuttingPlaneFromCut(cut, 8)

    if (!result.visualization || !result.visualization.position) {
      logger.warn('Failed to create cutting plane visualization')
      return
    }

    let crossSection: THREE.Object3D | undefined
    try {
      if (engine.currentProblem) {
        const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha.slice(0, 3)
        const slice = createCutCrossSection(engine.currentProblem.inequalities, alpha3D, cut.beta)
        if (slice) {
          crossSection = slice
        }
      }
    } catch (e) {
      logger.warn('Failed to create cut cross-section for preview:', e)
    }

    engine.currentCuttingPlane = {
      visualization: result.visualization,
      wireframe: result.wireframe,
      crossSection
    }

    if (crossSection) {
      engine.scene.add(result.visualization, result.wireframe, crossSection)
      engine.resourceCounters?.addOverlay?.(3)
    } else {
      engine.scene.add(result.visualization, result.wireframe)
      engine.resourceCounters?.addOverlay?.(2)
    }

    const planeResources = countObject3DResources(result.visualization)
    const wireResources = countObject3DResources(result.wireframe)
    const crossSectionResources = crossSection ? countObject3DResources(crossSection) : { geometries: 0, materials: 0 }
    engine.resourceCounters?.geometryCreated?.(planeResources.geometries + wireResources.geometries + crossSectionResources.geometries)
    engine.resourceCounters?.materialCreated?.(planeResources.materials + wireResources.materials + crossSectionResources.materials)

    engine.renderScheduler?.invalidate?.('cut-preview-shown')

    logger.log('Cutting plane preview shown with modern styling')
  } catch (error) {
    logger.error('Error showing cutting plane preview:', error as any)
  } finally {
    engine.performanceProbe?.markEnd?.('cut-preview')
  }
}

export function showCutConfirmationButtons(engine: CutWorkflowEngineContext): void {
  void engine
  useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_PREVIEW_READY' })
}

export async function confirmCut(engine: CutWorkflowEngineContext, isSimplex: boolean = false): Promise<void> {
  if (isBnBInteractionActive()) {
    uiManager.showNotification('Branch & Bound is active. Cannot confirm cut now.', 'info')
    return
  }

  if (!engine.pendingCut || !engine.currentProblem || engine.pendingCutIndex === -1) {
    logger.error('No pending cut to confirm')
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_APPLY_FAILED', error: 'No pending cut to confirm' })
    return
  }

  const cut = engine.pendingCut
  const cutIndex = engine.pendingCutIndex
  logger.log(`Executing confirmed cut: ${cut.alpha.map((c: number) => Math.round(c)).join('x + ')} <= ${Math.round(cut.beta)}`)

  const originalVolume = engine.polyhedronGroup ? engine.calculateCurrentPolyhedronVolume() : 0
  logger.log('Original volume before cut:', originalVolume)

  try {
    engine.cleanupCuttingPlane()
    await engine.executeCut(cut, originalVolume)

    engine.availableCuts.splice(cutIndex, 1)
    logger.log(`Removed applied cut from pool. ${engine.availableCuts.length} cuts remaining in pool.`)
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_APPLY_SUCCEEDED' })
  } catch (error) {
    logger.error('Error executing cut:', error as any)
    uiManager.showNotification(`Cut Failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
    useGameStore.getState().dispatchCutFlowEvent({
      type: 'CUT_APPLY_FAILED',
      error: error instanceof Error ? error.message : 'Unknown execution error',
    })
  } finally {
    engine.pendingCut = null
    engine.pendingCutIndex = -1
    engine.pendingSimplexConfig = null
    if (!isSimplex) engine.hideCutConfirmationButtons()
    useGameStore.getState().setAvailableCuts(engine.availableCuts)
  }
}

export function cancelCut(engine: CutWorkflowEngineContext, isSimplex: boolean = false): void {
  if (isBnBInteractionActive()) {
    uiManager.showNotification('Branch & Bound is active. Cut cancellation is handled automatically.', 'info')
    return
  }

  logger.log('Cut cancelled')
  engine.cleanupCuttingPlane()
  engine.pendingCut = null
  engine.pendingCutIndex = -1
  engine.pendingSimplexConfig = null

  if (!isSimplex) engine.hideCutConfirmationButtons()

  useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_CANCELLED' })

  uiManager.showNotification('Cut cancelled', 'info')
}

export function cleanupCuttingPlane(engine: CutWorkflowEngineContext): void {
  if (engine.currentCuttingPlane) {
    let overlayCount = 2
    let disposedGeometries = 0
    let disposedMaterials = 0
    engine.scene.remove(engine.currentCuttingPlane.visualization, engine.currentCuttingPlane.wireframe)
    if (engine.currentCuttingPlane.crossSection) {
      engine.scene.remove(engine.currentCuttingPlane.crossSection)
      overlayCount += 1
    }

    const visualizationDisposed = countAndDisposeObject3D(engine.currentCuttingPlane.visualization)
    disposedGeometries += visualizationDisposed.geometries
    disposedMaterials += visualizationDisposed.materials

    const wireframeDisposed = countAndDisposeObject3D(engine.currentCuttingPlane.wireframe)
    disposedGeometries += wireframeDisposed.geometries
    disposedMaterials += wireframeDisposed.materials

    if (engine.currentCuttingPlane.crossSection) {
      const crossSectionDisposed = countAndDisposeObject3D(engine.currentCuttingPlane.crossSection)
      disposedGeometries += crossSectionDisposed.geometries
      disposedMaterials += crossSectionDisposed.materials
    }

    engine.resourceCounters?.geometryDisposed?.(disposedGeometries)
    engine.resourceCounters?.materialDisposed?.(disposedMaterials)

    engine.currentCuttingPlane = null
    engine.resourceCounters?.clearOverlay?.(overlayCount)
    engine.renderScheduler?.invalidate?.('cut-preview-cleared')
    logger.log('Cutting plane cleaned up')
  }
}

export function hideCutConfirmationButtons(engine: CutWorkflowEngineContext): void {
  void engine
}

export async function executeCut(engine: CutWorkflowEngineContext, cut: CanonicalCut, originalVolume: number): Promise<void> {
  if (!engine.currentProblem) {
    throw new Error('Cannot execute cut: current problem is unavailable')
  }

  const currentProblem = engine.currentProblem

  if (engine.polyhedronGroup) {
    engine.polyManager.disposeGroup(engine.polyhedronGroup)
    engine.polyhedronGroup = null
    logger.log('Old polyhedron removed')
  }

  const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha.slice(0, 3)
  const newInequalities = [...currentProblem.inequalities, { alpha: alpha3D, beta: cut.beta }]

  engine.currentProblem = {
    A: [...currentProblem.A, alpha3D],
    b: [...currentProblem.b, cut.beta],
    c: currentProblem.c,
    inequalities: newInequalities
  }
  useGameStore.getState().setCurrentProblem(engine.currentProblem)
  logger.log('Added cut constraint to problem')

  const result = engine.cutService.applyCut(engine.currentProblem, cut, engine.polyhedronGroup, originalVolume, engine.initialObjectiveValue)
  engine.currentProblem = result.problem
  engine.polyhedronGroup = result.group
  const { newVolume: newVolumeVal, volumeReductionPct, currentObjectiveValue, objectiveReductionPct, optimalSolution } = result.metrics

  const newVolume = newVolumeVal
  const volumeReduction = volumeReductionPct
  const objectiveValueReduction = objectiveReductionPct

  logger.log('=== Objective Value Calculation ===')
  logger.log(`Initial objective value: ${engine.initialObjectiveValue}`)
  logger.log(`Current objective value: ${currentObjectiveValue}`)
  logger.log(`Objective value reduction: ${objectiveValueReduction.toFixed(1)}%`)
  logger.log('===================================')

  const baseCutType = cut.cgfType === 'gmi' ? 'GMI' :
    cut.cgfType === '2dsimplex' ? 'Simplex' :
      cut.cgfType === 'gomory' ? 'Gomory' :
        cut.cgfType === 'user' ? 'USER' : 'Unknown'

  const currentCount = (engine.cutTypeCounters.get(baseCutType) || 0) + 1
  engine.cutTypeCounters.set(baseCutType, currentCount)
  const cutType = `${currentCount} ${baseCutType}`

  const historyItem = {
    cutNumber: engine.customCutsLimit - engine.cutsRemaining + 1,
    cut: cut,
    cutType: cutType,
    volumeReduction: volumeReduction,
    objectiveValueReduction: objectiveValueReduction,
    originalVolume: originalVolume,
    newVolume: newVolume,
    originalObjectiveValue: engine.initialObjectiveValue,
    newObjectiveValue: currentObjectiveValue,
    optimalSolution: optimalSolution,
    timestamp: new Date()
  }
  engine.cutsHistory.push(historyItem)

  engine.cutsRemaining--
  logger.log('Cut applied successfully!')
  logger.log(`Original volume: ${originalVolume.toFixed(4)}`)
  logger.log(`New volume: ${newVolume.toFixed(4)}`)
  logger.log(`Volume reduction: ${volumeReduction.toFixed(1)}%`)

  if (visualEffectsManager) {
    visualEffectsManager.createExplosionEffect(new THREE.Vector3(0, 0, 0), 1.2)
    visualEffectsManager.screenShake(engine.camera, 0.05, 300)

    const scoreGain = Math.round(volumeReduction * 10)
    visualEffectsManager.createFloatingNumber(
      new THREE.Vector3(2, 2, 0),
      scoreGain,
      '#2ecc71'
    )
  }

  uiManager.showNotification(`Cut Applied! Volume Reduction: ${volumeReduction.toFixed(1)}%`, 'success')
  engine.updateUI()
  engine.renderScheduler?.invalidate?.('cut-applied')

  appBus.emit('cut-applied', {
    cut,
    metrics: {
      newVolume,
      volumeReductionPct: volumeReduction,
      currentObjectiveValue,
      objectiveReductionPct: objectiveValueReduction,
    },
    remainingCuts: engine.cutsRemaining,
  })
  appBus.emit('history-updated', { history: engine.cutsHistory })
  engine.updateBnBStartAvailability()

  if (engine.cutsRemaining > 0) {
    logger.log(`🔄 [Cut Applied] ${engine.cutsRemaining} cuts remaining, cut regeneration DISABLED to prevent geometry destruction`)
    logger.log('⚠️  Note: Cut pool will not be refreshed until regeneration logic is fixed')
  } else {
    logger.log('🏁 [Cut Applied] No cuts remaining, skipping regeneration')
  }

  if (engine.cutsRemaining <= 0) {
    engine.endGame()
  }
}

export function reconstructCompleteProblemFromHistory(engine: CutWorkflowEngineContext): IntegerProblem {
  if (!engine.originalProblem) {
    throw new Error('Cannot reconstruct: originalProblem is null')
  }

  logger.log('🔧 Reconstructing complete problem from original + cuts history...')
  logger.log(`   Original problem: ${engine.originalProblem.A.length} constraints`)
  logger.log(`   Applied cuts in history: ${engine.cutsHistory.length} cuts`)

  const reconstructedProblem = cloneIntegerProblem(engine.originalProblem, { includeFracData: false })

  for (let i = 0; i < engine.cutsHistory.length; i++) {
    const historyItem = engine.cutsHistory[i]
    const cut = historyItem.cut

    logger.log(`   Adding cut ${i + 1}: ${cut.alpha.map((c: number) => c.toFixed(3)).join('x + ')}x ≤ ${cut.beta.toFixed(3)}`)

    const alpha3D = cut.alpha.length === 2 ? [...cut.alpha, 0] : cut.alpha.slice(0, 3)
    reconstructedProblem.A.push(alpha3D)
    reconstructedProblem.b.push(cut.beta)
    reconstructedProblem.inequalities.push({ alpha: alpha3D, beta: cut.beta })
  }

  logger.log(`   Reconstructed problem: ${reconstructedProblem.A.length} total constraints`)
  logger.log(`   = ${engine.originalProblem.A.length} original + ${engine.cutsHistory.length} cuts`)

  return reconstructedProblem
}

export async function refreshAvailableCuts(engine: CutWorkflowEngineContext): Promise<void> {
  if (!engine.originalProblem) {
    logger.error('Cannot refresh cuts: no original problem available')
    uiManager.showNotification('No original problem available to refresh cuts', 'error')
    return
  }

  logger.log('🔄 Refreshing available cuts using correct flow...')

  try {
    const completeProblem = engine.reconstructCompleteProblemFromHistory()
    const lpSolutionCheck = getCutInfo(completeProblem)
    logger.log('🔍 LP solution check for reconstructed problem:', lpSolutionCheck)

    if (!lpSolutionCheck.hasFractionalSolution) {
      logger.log('🎉 Optimal IP solution found!')
      engine.showOptimalSolutionCongratulations(lpSolutionCheck.optimalValue)
      return
    }

    logger.log('🔪 Generating cuts from reconstructed problem (with redundancy detection)...')
    const { cuts } = generateAllCuts(completeProblem, ['gmi', 'gomory', '2dsimplex'])

    if (cuts.length === 0) {
      uiManager.showNotification('No new cuts available for current problem state', 'info')
      return
    }

    const markedCuts = cuts.map(cut => ({
      ...cut,
      fromReconstructedProblem: true,
      reconstructedProblem: completeProblem
    }))
    engine.availableCuts = markedCuts

    logger.log('🔄 [CRITICAL FIX] Synchronizing currentProblem with reconstructed problem...')
    logger.log(`   Before sync: currentProblem has ${engine.currentProblem?.A.length} constraints`)
    logger.log(`   After sync: currentProblem will have ${completeProblem.A.length} constraints`)

    engine.currentProblem = completeProblem

    if (engine.polyhedronGroup) {
      engine.scene.remove(engine.polyhedronGroup)
      engine.polyhedronGroup.children.forEach((child: any) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          if (Array.isArray(child.material)) {
            child.material.forEach((mat: any) => mat.dispose())
          } else {
            child.material.dispose()
          }
        }
      })
      engine.polyhedronGroup = null
      logger.log('🗑️  Removed old polyhedron for synchronization')
    }

    if (engine.polyhedronGroup) {
      engine.polyManager.disposeGroup(engine.polyhedronGroup)
    }
    engine.polyhedronGroup = engine.polyManager.buildGroupFromInequalities(engine.currentProblem.inequalities)
    engine.polyManager.addIntegerPointsAndAxes(engine.polyhedronGroup, engine.currentProblem, { showHull: false })
    logger.log('🔄 Created new polyhedron from synchronized problem')

    const cutsByType = cuts.reduce((acc, cut) => {
      acc[cut.cgfType] = (acc[cut.cgfType] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    logger.log('🔪 Refreshed Cut Generation Summary:')
    Object.entries(cutsByType).forEach(([type, count]) => {
      logger.log(`   ${type}: ${count} cuts`)
    })
    logger.log(`   Total cuts available: ${engine.availableCuts.length}`)
    logger.log('✅ Refresh completed with correct P\' → P\'\' → candidate rows flow')
    logger.log('✅ currentProblem synchronized with refresh cuts state')

    uiManager.createCutButtons(engine.availableCuts)

    uiManager.showNotification(
      `✅ Refreshed ${cuts.length} new cuts from updated problem state!`,
      'success'
    )
  } catch (error) {
    logger.error('Error refreshing cuts:', error)
    uiManager.showNotification(
      `Failed to refresh cuts: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'error'
    )
  }
}

export function showOptimalSolutionCongratulations(engine: CutWorkflowEngineContext, optimalValue: number): void {
  const currentVolume = engine.calculateCurrentPolyhedronVolume()
  const volumeReduction = engine.initialVolume > 0 ? ((engine.initialVolume - currentVolume) / engine.initialVolume) * 100 : 0
  const cutsApplied = engine.cutsHistory.length

  useGameStore.getState().showOptimalSolutionModal({
    optimalValue,
    cutsApplied,
    volumeReduction,
    initialObjectiveValue: engine.initialObjectiveValue,
    initialIPOptimalValue: engine.initialIPOptimalValue,
  })
  uiManager.showNotification('Optimal integer solution found.', 'success')
}

export function validateUserSimplexData(engine: CutWorkflowEngineContext, cut: CanonicalCut, problem: IntegerProblem): boolean {
  const fVals = cut.fVals ?? []
  const rays = cut.rays ?? []

  if (fVals.length !== 2 || rays.length !== 2) {
    return false
  }

  try {
    let validationProblem: IntegerProblem
    let problemSource: string

    if ((cut as any).fromReconstructedProblem && (cut as any).reconstructedProblem) {
      validationProblem = (cut as any).reconstructedProblem
      problemSource = 'reconstructed'
    } else {
      validationProblem = engine.originalProblem || problem
      problemSource = engine.originalProblem ? 'original' : 'current'
    }

    logger.log(`🔍 [Simplex Validation] Using ${problemSource} problem for validation`)
    const { A_int, b_int } = ensureIntegerMatrix(validationProblem.A, validationProblem.b)
    const { candidateRowIndices, tableau } = getCandidateTableauRows(A_int, b_int, validationProblem.c)
    logger.log(`🔍 [Simplex Validation] Candidate rows: [${candidateRowIndices.join(', ')}]`)

    if (!candidateRowIndices.includes(cut.sourceRow)) {
      logger.warn(`Source row ${cut.sourceRow} not in candidate rows:`, candidateRowIndices)
      return false
    }

    if (cut.secondSourceRow !== undefined && !candidateRowIndices.includes(cut.secondSourceRow)) {
      logger.warn(`Second source row ${cut.secondSourceRow} not in candidate rows:`, candidateRowIndices)
      return false
    }

    logger.log('🔍 [Simplex Validation] Skipping strict RHS validation for existing cuts')
    logger.log(`🔍 [Simplex Validation] Cut has stored fVals: [${fVals.join(', ')}] and rays: [${rays.join(', ')}]`)

    if (cut.sourceRow >= tableau.tableau.length ||
      (cut.secondSourceRow !== undefined && cut.secondSourceRow >= tableau.tableau.length)) {
      logger.warn('Source rows exceed tableau dimensions')
      return false
    }

    logger.log('🔍 [Simplex Validation] Basic validation passed - rows exist in tableau')
    logger.log('[User Simplex] Data validation passed')
    return true
  } catch (error) {
    logger.error('Error validating User Simplex data:', error)
    return false
  }
}

export function createCutWithMuValue(
  engine: CutWorkflowEngineContext,
  baseCut: CanonicalCut,
  muValue: number,
  fVals: number[],
  rays: number[][]
): CanonicalCut {
  let muForCalculation = muValue
  if (muValue === 0) {
    muForCalculation = 0.0001
  } else if (muValue === 1) {
    muForCalculation = 0.9999
  }

  logger.log(`[User Simplex] Creating cut with μ: ${muValue} -> calc: ${muForCalculation}`)

  const convertToFractionArray = (arr: number[]): Fraction[] => arr.map(num => Fraction.fromDecimal(num))

  const coeffFracs = pi_2dsimplex_with_mu(
    convertToFractionArray(fVals),
    rays.map(row => convertToFractionArray(row)),
    Fraction.fromDecimal(muForCalculation)
  ) as Fraction[]

  logger.log('[User Simplex] New coeffs from pi_2dsimplex_with_mu:', coeffFracs.map(c => c.toNumber()))

  let problemForTranslation: IntegerProblem
  if ((baseCut as any).fromReconstructedProblem && (baseCut as any).reconstructedProblem) {
    problemForTranslation = (baseCut as any).reconstructedProblem
    logger.log('[User Simplex] Using reconstructed problem for translation (refresh cut)')
  } else {
    const fallbackProblem = engine.originalProblem ?? engine.currentProblem
    if (!fallbackProblem) {
      throw new Error('No problem available for User Simplex translation')
    }
    problemForTranslation = fallbackProblem
    logger.log('[User Simplex] Using original problem for translation (original cut)')
  }
  logger.log(`[User Simplex] Problem for translation - A: ${problemForTranslation.A.length}x${problemForTranslation.A[0]?.length}, b: ${problemForTranslation.b.length}`)

  const { alpha, beta } = translateCutToCanonicalFormStable(problemForTranslation.A, problemForTranslation.b, coeffFracs)
  logger.log('[User Simplex] New alpha/beta from translate:', { alpha, beta })

  return {
    ...baseCut,
    alpha,
    beta,
    bestMu: muValue
  }
}

export function openUserSimplexConfigurator(engine: CutWorkflowEngineContext, cut: CanonicalCut, cutIndex: number): void {
  if (!engine.currentProblem) return

  const fVals = cut.fVals ?? []
  const rays = cut.rays ?? []
  if (fVals.length !== 2 || rays.length !== 2) {
    logger.error('User Simplex cut missing fVals or rays metadata')
    return
  }

  logger.log('[User Simplex] Opening configurator with cut:', { fVals, rays, bestMu: cut.bestMu, problemA: engine.currentProblem.A, problemB: engine.currentProblem.b })

  const isDataValid = engine.validateUserSimplexData(cut, engine.currentProblem)
  if (!isDataValid) {
    logger.error('[User Simplex] fVals/rays data inconsistent with current problem - refreshing cut data')
    uiManager.showNotification('Cut data is stale. Please regenerate cuts.', 'error')
    return
  }

  const convertToFractionArray = (arr: number[]): Fraction[] => arr.map(num => Fraction.fromDecimal(num))

  let currentMu: number = cut.bestMu ?? 0.5

  if (cut.bestMu === undefined) {
    logger.log('[User Simplex] bestMu missing, recalculating optimal μ value...')
    try {
      const result = pi_2dsimplex(
        convertToFractionArray(fVals),
        rays.map(row => convertToFractionArray(row)),
        true
      )

      if (!Array.isArray(result)) {
        currentMu = result.optimalMu.toNumber()
        logger.log('[User Simplex] Recalculated optimal μ:', currentMu)
      } else {
        logger.warn('[User Simplex] Failed to recalculate optimal μ, using default 0.5')
        currentMu = 0.5
      }
    } catch (error) {
      logger.error('[User Simplex] Error recalculating optimal μ:', error)
      currentMu = 0.5
    }
  }

  logger.log('[User Simplex] Using μ value:', currentMu, 'for slider initialization')

  const initialCutForPreview = engine.createCutWithMuValue(cut, currentMu, fVals, rays)

  engine.pendingSimplexConfig = { baseCut: cut, fVals, rays }
  engine.pendingCut = initialCutForPreview
  engine.pendingCutIndex = cutIndex
  engine.showCuttingPlanePreview(initialCutForPreview)
  useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_MU_ADJUST_REQUIRED', mu: currentMu })
}

export function handleCutMuChange(engine: CutWorkflowEngineContext, mu: number): void {
  if (isBnBInteractionActive()) {
    return
  }

  if (!engine.pendingSimplexConfig || !engine.pendingCut) {
    return
  }

  const { baseCut, fVals, rays } = engine.pendingSimplexConfig
  const nextMu = Number.isFinite(mu) ? mu : 0.5

  const updatedCut = engine.createCutWithMuValue(baseCut, nextMu, fVals, rays)
  engine.cleanupCuttingPlane()
  engine.showCuttingPlanePreview(updatedCut)
  engine.pendingCut = updatedCut
  useGameStore.getState().dispatchCutFlowEvent({ type: 'MU_CHANGED', mu: nextMu })
}
